import { useCallback, useEffect, useState } from "react";
import { Building2, Download, TrendingUp, Wallet } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";
import { BooksAdviceSection, BooksClientsSection, BooksDocumentsSection, BooksExpensesSection, BooksFlowGuideSection, BooksLayoutSection, BooksLegalSection, BooksNotificationsSection, BooksSmtpSection, BooksTemplatesSection, BooksTimeSection, BooksVatSection } from "./BooksSections";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CARD = "border border-white/10 bg-coal p-6";
const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";
const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";

const COMPANY_FIELDS = [
    ["address", "Adres"],
    ["postal_city", "Postcode & plaats"],
    ["kvk", "KvK-nummer"],
    ["btw_number", "BTW-nummer"],
    ["iban", "IBAN"],
    ["phone", "Telefoon"],
    ["email", "E-mail"],
];

const MONTH_NL = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

export const AdminBooksTab = () => {
    const [data, setData] = useState(null);
    const [company, setCompany] = useState({});
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            const [ov, co] = await Promise.all([
                platformClient.get("/admin/billing/overview"),
                platformClient.get("/admin/settings/company"),
            ]);
            setData(ov.data);
            setCompany(co.data || {});
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const saveCompany = async () => {
        setError("");
        try {
            await platformClient.put("/admin/settings/company", company);
            setNotice("Bedrijfsgegevens opgeslagen — verschijnen op facturen en contracten");
            setTimeout(() => setNotice(""), 5000);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const exportCsv = async () => {
        const { data } = await platformClient.get("/admin/billing/export.csv", { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "clarke-boekhouding-export.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!data) {
        return <p data-testid="books-loading" className="mt-10 text-xs uppercase tracking-[0.3em] text-ash">Boekhouding laden…</p>;
    }

    const maxRevenue = Math.max(1, ...data.revenue_by_month.map((m) => m.total));

    return (
        <div data-testid="admin-books-tab" className="mt-8">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <div className={CARD} data-testid="books-mrr">
                    <p className={LABEL}><TrendingUp className="mr-2 inline h-3.5 w-3.5 text-gold" />MRR</p>
                    <p className="mt-3 font-display text-3xl text-gold">{euro(data.mrr)}</p>
                    <p className="mt-1 text-xs text-ash">{data.active_subscriptions} actieve abonnementen</p>
                </div>
                <div className={CARD} data-testid="books-arr">
                    <p className={LABEL}><TrendingUp className="mr-2 inline h-3.5 w-3.5 text-gold" />ARR</p>
                    <p className="mt-3 font-display text-3xl text-bone">{euro(data.arr)}</p>
                    <p className="mt-1 text-xs text-ash">jaarlijkse run-rate</p>
                </div>
                <div className={CARD} data-testid="books-open">
                    <p className={LABEL}><Wallet className="mr-2 inline h-3.5 w-3.5 text-gold" />Openstaand</p>
                    <p className="mt-3 font-display text-3xl text-bone">{euro(data.open_amount)}</p>
                    <p className="mt-1 text-xs text-ash">
                        {data.overdue_count ? <span className="text-red-300">{data.overdue_count} verlopen</span> : "niets verlopen"}
                    </p>
                </div>
                <div className={`${CARD} flex flex-col justify-between`} data-testid="books-export">
                    <p className={LABEL}>Voor je accountant</p>
                    <button
                        data-testid="books-export-csv"
                        onClick={exportCsv}
                        className="mt-3 flex items-center gap-2 border border-gold/50 px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                    >
                        <Download className="h-3.5 w-3.5" /> Exporteer CSV
                    </button>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className={CARD}>
                    <p className={LABEL}>Omzet per maand (laatste 12 maanden)</p>
                    <div className="mt-6 flex h-40 items-end gap-2">
                        {data.revenue_by_month.map((m) => (
                            <div key={m.month} data-testid={`books-month-${m.month}`} className="group flex flex-1 flex-col items-center gap-2">
                                <span className="text-[9px] text-ash opacity-0 transition-opacity group-hover:opacity-100">{euro(m.total)}</span>
                                <div
                                    className={`w-full transition-all duration-500 ${m.total > 0 ? "bg-gold" : "bg-white/10"}`}
                                    style={{ height: `${Math.max(2, (m.total / maxRevenue) * 100)}%` }}
                                />
                                <span className="text-[9px] uppercase tracking-wide text-ash">
                                    {MONTH_NL[Number(m.month.slice(5, 7)) - 1]} {m.month.slice(2, 4)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={CARD}>
                    <p className={LABEL}>Omzet per klant</p>
                    {data.per_tenant.length === 0 ? (
                        <p className="mt-4 text-sm text-ash">Nog geen abonnementen ingericht.</p>
                    ) : (
                        <div className="mt-4 space-y-2">
                            {data.per_tenant.map((t) => (
                                <div key={t.tenant_id} data-testid={`books-tenant-${t.tenant_id}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2 text-xs">
                                    <span className="text-bone">{t.name}</span>
                                    <span className="text-ash">
                                        {euro(t.monthly_price)}/mnd · totaal {euro(t.invoiced_total)}
                                        {t.open_total > 0 ? <span className="text-gold"> · {euro(t.open_total)} open</span> : null}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 border border-white/10 bg-coal p-6 md:p-8">
                <p className={LABEL}>
                    <Building2 className="mr-2 inline h-3.5 w-3.5 text-gold" />
                    Mijn bedrijfsgegevens (verschijnen op jouw facturen en contracten)
                </p>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    {COMPANY_FIELDS.map(([key, label]) => (
                        <label key={key} className="block">
                            <span className={LABEL}>{label}</span>
                            <input
                                data-testid={`company-${key}`}
                                value={company[key] || ""}
                                onChange={(e) => setCompany({ ...company, [key]: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                    ))}
                </div>
                {error ? <p data-testid="company-error" className="mt-4 text-sm text-red-400">{error}</p> : null}
                {notice ? <p data-testid="company-notice" className="mt-4 text-xs uppercase tracking-[0.15em] text-gold">{notice}</p> : null}
                <button
                    data-testid="company-save"
                    onClick={saveCompany}
                    className="mt-5 border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                >
                    Gegevens opslaan
                </button>
            </div>

            <BooksFlowGuideSection />
            <BooksNotificationsSection />
            <BooksDocumentsSection />
            <BooksLayoutSection />
            <BooksSmtpSection />
            <BooksTemplatesSection />
            <BooksClientsSection />
            <BooksVatSection />
            <BooksExpensesSection />
            <BooksTimeSection />
            <BooksAdviceSection />
            <BooksLegalSection />
        </div>
    );
};
